/* AccountModuleV2.js */
/* @author: anghello */

import React, { useCallback, useEffect, useState } from 'react';
import { Button, Card, CardHeader, CardBody, Input, Label, FormGroup, Form, Row, Col } from 'reactstrap';
import WAM from '../../../services/WAM';
import { useAuth } from '../../../Context/Auth/AuthContext';
import Strings from '../../../translations/Translations';
import { toast } from 'react-toastify';
import { useForm } from 'react-hook-form';
import * as Yup from 'yup';

const schemaUser = Yup.object().shape({
  account_name: Yup.string().required(Strings('name_is_required'))
});

export default function AccountModuleV2({ changeEmailCallback, changePasswordCallback }) {
  const { authenticated, toggleLoading } = useAuth();

  const { register, errors, handleSubmit } = useForm({
    validationSchema: schemaUser
  });

  const [userData, setUserData] = useState({
    name: '',
    biography: '',
    website: ''
  });

  useEffect(() => {
    async function getData() {
      const username = authenticated.un;
      const al = authenticated.al;
      toggleLoading(true);

      try {
        const response = await WAM.getUserProfile({ username, al });
        toggleLoading(false);

        if (response.result === 'success') {
          const data = WAM.listToJson(response.list);

          const userDataToSet = {};
          userDataToSet.name = (data.obj2) ? data.obj2 : '';
          userDataToSet.biography = (data.obj4) ? data.obj4 : '';
          userDataToSet.website = (data.obj6) ? data.obj6 : '';
          setUserData(userDataToSet);
        } else {
          const message = (response && response.error) ? response.error : Strings('error_on_request');
          toast.error(message);
        }
      } catch (err) {
        toggleLoading(false);
        toast.error(Strings('general_error'));
      }
    }
    getData();
  }, [authenticated, toggleLoading, setUserData]);

  const onSubmit = useCallback(async (data) => {
    toggleLoading(true);

    const requestData = {};
    requestData.username = authenticated.un;
    requestData.al = authenticated.al;

    if (data.account_name) requestData.fullName = data.account_name;
    if (data.account_biography) requestData.description = data.account_biography;
    if (data.account_website) requestData.url = data.account_website;

    try {
      const response = await WAM.setUserProfile(requestData);

      toggleLoading(false);

      if (response.result === 'success') {
        toast.success(Strings('info_updated'));
      } else {
        const message = (response && response.error) ? response.error : Strings('error_on_update');
        toast.error(message);
      }
    } catch (err) {
      toggleLoading(false);
      toast.error(Strings('general_error'));
    }
  }, [authenticated, toggleLoading]);

  return (
    <Card>
      <CardHeader id="headingOne">
        <h3 className="form-heading text-capitalize">
          {Strings('account_information')}
        </h3>
      </CardHeader>
      <CardBody>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <FormGroup>
            <Label for="account_name">{Strings('name')}</Label>
            <Input
              type="text"
              name="account_name"
              id="account_name"
              placeholder={Strings('enter_your_name')}
              defaultValue={userData.name}
              innerRef={register}
            />
            {errors.account_name && (
              <p className="error-validation text-left">
                {errors.account_name.message}
              </p>
            )}
          </FormGroup>
          <FormGroup>
            <Label for="account_biography">{Strings('biography')}</Label>
            <Input
              type="textarea"
              name="account_biography"
              id="account_biography"
              placeholder={Strings('enter_your_biography')}
              defaultValue={userData.biography}
              innerRef={register}
            />
          </FormGroup>
          <FormGroup>
            <Label for="account_website">{Strings('website')}</Label>
            <Input
              type="text"
              name="account_website"
              id="account_website"
              placeholder={Strings('enter_your_website')}
              defaultValue={userData.website}
              innerRef={register}
            />
          </FormGroup>
          <Button
            color="primary"
            className="mt-1"
            onClick={handleSubmit(onSubmit)}
          >
            {Strings('save')}
          </Button>
        </Form>

        {(changeEmailCallback || changePasswordCallback) && (
          <Row>
            <Col className='d-flex justify-content-start mt-3' style={{gap: '10px'}}>
              {changeEmailCallback && (
                <p
                  className='text-primary pointer'
                  onClick={changeEmailCallback}
                  style={{textDecoration: 'underline'}}
                >
                  {Strings('change_email')}
                </p>
              )}
              {changePasswordCallback && (
                <p
                  className='text-primary pointer'
                  onClick={changePasswordCallback}
                  style={{textDecoration: 'underline'}}
                >
                  {Strings('change_password')}
                </p>
              )}
            </Col>
          </Row>
        )}
      </CardBody>
    </Card>
  );
}
